/**
 * TRACKING_LINK_PARAMS is the set of tracking link parameters we capture: Singular's, plus Smadex's click id.
 * These params are documented here:
 * https://support.singular.net/hc/en-us/articles/360018879491-Tracking-Link-Parameters
 *
 * This list lives in common-universal so both frontend and backend services can reference the same whitelist.
 */
export const TRACKING_LINK_PARAMS = [
  'gad_source',
  'gad_campaign',
  'gad_campaignid',
  'gbraid',
  'gclid',
  'wpsrc',
  'wpcid',
  'wpscid',
  'wpcrid',
  'wpkwid',
  'wpkwn',
  'wpkmatch',
  'wpsnetn',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
  // Smadex's click id; the server posts conversions back against it.
  'smx_click_id'
] as const

export type TrackingLinkParam = (typeof TRACKING_LINK_PARAMS)[number]

// Smadex's "order": one tracking group for both the web pixel and the S2S postbacks.
export const SMADEX_TRACKING_GROUP_ID = '116157'

export enum AdNetwork {
  Smadex = 'smadex'
}

const TRACKING_LINK_PARAMS_SET: ReadonlySet<string> = new Set(TRACKING_LINK_PARAMS)

/** Maximum byte length for a tracking param value (Mixpanel's limit). */
const MAX_PARAM_VALUE_BYTES = 255

/**
 * Pattern that matches characters unsafe in tracking param values.
 * Rejects HTML tags, script injection, control characters, and other dangerous patterns.
 */
const UNSAFE_VALUE_PATTERN = /[<>"'`\x00-\x1f\x7f]/

/**
 * Sanitizes a single tracking param value.
 * Returns undefined if the value is invalid or potentially dangerous.
 */
export function sanitizeTrackingParamValue(value: string): string | undefined {
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return undefined
  }

  if (UNSAFE_VALUE_PATTERN.test(trimmed)) {
    return undefined
  }

  // Enforce byte length limit
  const encoder = new TextEncoder()
  if (encoder.encode(trimmed).length > MAX_PARAM_VALUE_BYTES) {
    return undefined
  }

  return trimmed
}

/**
 * Extracts only known tracking params from a query object, sanitizing each value.
 * Rejects any key not in TRACKING_LINK_PARAMS.
 */
export function getTrackingParamsFromQuery(
  query: Record<string, string | string[] | undefined>
): Record<string, string> {
  const result: Record<string, string> = {}

  for (const key of TRACKING_LINK_PARAMS) {
    const raw = query[key]
    if (raw === undefined) {
      continue
    }

    const value = Array.isArray(raw) ? raw[0] : raw
    if (value === undefined) {
      continue
    }

    const sanitized = sanitizeTrackingParamValue(value)
    if (sanitized !== undefined) {
      result[key] = sanitized
    }
  }

  return result
}

/**
 * Appends tracking params to a URL, properly URI-encoding values.
 * Only appends params that are in the TRACKING_LINK_PARAMS whitelist.
 */
export function appendTrackingParamsToUrl(baseUrl: string, trackingParams: Record<string, string>): string {
  const entries = Object.entries(trackingParams).filter(([key]) => TRACKING_LINK_PARAMS_SET.has(key))

  if (entries.length === 0) {
    return baseUrl
  }

  const paramString = entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&')

  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}${paramString}`
}

/**
 * Returns true if the given key is a known tracking link parameter.
 */
export function isTrackingParam(key: string): boolean {
  return TRACKING_LINK_PARAMS_SET.has(key)
}

/**
 * The `utm_*` subset of the sanitized tracking params. The full whitelist also carries per-click ad
 * identifiers (`gclid`, `gbraid`, `wpcid`, ...) that are joinable back to a single ad click, so an
 * anonymous event must carry only the campaign-label keys.
 */
export function utmParamsOnly(trackingParams: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(trackingParams).filter(([key]) => isUtmParam(key)))
}

export function withoutUtmParams(trackingParams: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(trackingParams).filter(([key]) => !isUtmParam(key)))
}

function isUtmParam(key: string): boolean {
  return key.startsWith('utm_')
}
